// src/pages/ReceivedAmount/ReceivedAmountForm.tsx
import React, { useState, useEffect } from 'react';
import { PaymentMethod } from '../../types/invoiceTypes';
import Button from '../../components/common/Button/Button';
import styles from './ReceivedAmountForm.module.scss';
import customerService from '../../services/customerService';
import receivedAmountService from '../../services/receivedAmountService';
import invoiceService from '../../services/invoiceService'; // Added import for invoiceService
import accountHeadService from '../../services/accountHeadService'; // Added import for accountHeadService
import { formatCurrency, formatDate } from '../../utils/helpers';


const paymentMethods: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'card', label: 'Card' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
];

interface ReceivedAmountFormProps {
  onSuccess?: (data: any) => void;
  editingItem?: any; // Add this line
}

const ReceivedAmountForm: React.FC<ReceivedAmountFormProps> = ({ onSuccess, editingItem }) => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<any[]>([]);
  const [form, setForm] = useState({
    customerId: '',
    invoiceId: '',
    amount: '',
    paymentDate: new Date().toLocaleDateString('en-CA'),
    paymentMethod: 'cash' as PaymentMethod,
    notes: 'cash',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountHeads, setAccountHeads] = useState<any[]>([]);
  const [selectedAccountHead, setSelectedAccountHead] = useState('');
  // const [useInvoice, setUseInvoice] = useState(false);

  console.log("form:::::::", accountHeads)

  const getCurrentTimeString = () => {
    const now = new Date();
    let hours = now.getHours();
    const minutes = now.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 -> 12
    const strMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `(Time - ${hours}:${strMinutes} ${ampm})`;
  };


  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const customers = await customerService.getCustomers();
        setCustomers(Array.isArray(customers) ? customers : []);
      } catch (error) {
        console.error('Failed to load customers:', error);
        setCustomers([]);
      }

    };

    fetchCustomers();
  }, []);

  useEffect(() => {
    if (form.customerId) {
      invoiceService.getInvoicesByCustomer(form.customerId)
        .then((customerInvoices) => {
          setInvoices(Array.isArray(customerInvoices) ? customerInvoices : []);
          setFilteredInvoices(Array.isArray(customerInvoices) ? customerInvoices : []);
          // Auto-select invoice if only one exists
          if (customerInvoices.length === 1) {
            setForm(prev => ({ ...prev, invoiceId: customerInvoices[0].id }));
          }
        })
        .catch(() => {
          setInvoices([]);
          setFilteredInvoices([]);
        });
    } else {
      setInvoices([]);
      setFilteredInvoices([]);
    }
  }, [form.customerId]);

  useEffect(() => {
    if (editingItem) {
      // Format the payment date properly for the date input
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return new Date().toLocaleDateString('en-CA');
        
        try {
          const date = new Date(dateString);
          date.setDate(date.getDate() + 1);
          return date.toLocaleDateString('en-CA');
        } catch {
          return new Date().toLocaleDateString('en-CA');
        }
      };

      setForm({
        customerId: editingItem.customer_id || editingItem.customerId || '',
        // invoiceId: editingItem.invoice_id || editingItem.invoiceId || '',
        invoiceId: '',
        amount: editingItem.amount?.toString() || '',
        paymentDate: formatDateForInput(editingItem.payment_date || editingItem.paymentDate),
        paymentMethod: editingItem.payment_method || editingItem.paymentMethod || 'cash',
        notes: editingItem.notes || '',
      });
      setSelectedAccountHead(editingItem.account_head_id || '');
    }
  }, [editingItem]);

  // useEffect(() => {
  //   console.log('InvoiceId changed:', form.invoiceId, invoices);
  //   if (form.invoiceId) {
  //     const selected = invoices.find(i => String(i.id) === String(form.invoiceId));
  //     console.log('Selected invoice:', selected);
  //     if (selected) {
  //       setForm(prev => ({
  //         ...prev,
  //         amount: selected.total_amount ? String(selected.total_amount) : ''
  //       }));
  //     }
  //   } 
  //   // else {
  //   //   // Optionally clear the amount if no invoice is selected
  //   //   setForm(prev => ({
  //   //     ...prev,
  //   //     amount: ''
  //   //   }));
  //   // }
  // }, [form.invoiceId, invoices]);

  useEffect(() => {
    // Fetch account heads
    const fetchAccountHeads = async () => {
      try {
        const data = await accountHeadService.getAccountHeads();
        setAccountHeads(Array.isArray(data) ? data : []);
      } catch {
        setAccountHeads([]);
      }
    };
    fetchAccountHeads();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updatedForm = { ...prev, [name]: value };
  
      // If payment method changes, also set notes
      if (name === "paymentMethod") {
        const baseNote = paymentMethods.find(pm => pm.value === value)?.label || value;
        updatedForm.notes = `${baseNote}`;
      }
  
      return updatedForm;
    });
    
    // Clear success/error when form changes
    if (success) setSuccess(false);
    if (error) setError(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Clear success/error when form changes
    if (success) setSuccess(false);
    if (error) setError(null);
  };

  const validateForm = () => {
    if (!form.customerId) return 'Please select a customer';
    if (!form.amount || parseFloat(form.amount) <= 0) return 'Please enter a valid amount greater than 0';
    if (!form.paymentDate) return 'Please select a payment date';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
  
    if (!form.amount || parseFloat(form.amount) <= 0) {
      setError('Amount must be greater than 0');
      return;
    }
  
    setIsSubmitting(true);
    setError(null);
  
    try {
      // Append time to notes
      const notesWithTime = form.notes
        ? `${form.notes} ${getCurrentTimeString()}`
        : getCurrentTimeString();
  
      const payload: any = {
        customer_id: form.customerId,
        amount: parseFloat(form.amount),
        payment_date: form.paymentDate,
        payment_method: form.paymentMethod,
        notes: notesWithTime,
        account_head_id: selectedAccountHead,
      };
  
      if (editingItem) {
        await receivedAmountService.update(editingItem.id, payload);
      } else {
        await receivedAmountService.recordPayment(payload);
      }
  
      setSuccess(true);
  
      if (onSuccess) onSuccess(payload);
  
      setTimeout(() => {
        setForm({
          customerId: '',
          invoiceId: '',
          amount: '',
          paymentDate: new Date().toLocaleDateString('en-CA'),
          paymentMethod: 'cash',
          notes: '',
        });
        setSuccess(false);
      }, 3000);
  
    } catch (err) {
      setError('Failed to save payment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  

  // const selectedInvoice = invoices.find(i => i.id === form.invoiceId);

  return (
    <div className={styles.container}>
      <form onSubmit={handleSubmit} className={styles.form} autoComplete="off">
        <div className={styles.formFields}>
          {/* Customer Selection */}
          <div className={styles.formGroup}>
            <label htmlFor="customerId" className={styles.label}>
              Customer <span className={styles.required}>*</span>
            </label>
            <select
              id="customerId"
              name="customerId"
              value={form.customerId}
              onChange={handleChange}
              required
              className={styles.select}
              disabled={!!editingItem} // Disable if editing
            >
              <option value="">Select a customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
          </div>
          {/* Invoice Checkbox */}
          {/* <div className={styles.formGroup}>
            <label style={{ color: 'black' }}>
              <input
                type="checkbox"
                checked={useInvoice}
                onChange={e => {
                  setUseInvoice(e.target.checked);
                  if (!e.target.checked) {
                    setForm(prev => ({ ...prev, invoiceId: '' }));
                  }
                }}
                style={{ marginRight: 8 }}
              />
              Link to Invoice
            </label>
          </div> */}
          {/* Invoice Selection (conditional) */}
          {/* {useInvoice && (
            <div className={styles.formGroup}>
              <label htmlFor="invoiceId" className={styles.label}>
                Invoice (Optional)
              </label>
              <select
                id="invoiceId"
                name="invoiceId"
                value={form.invoiceId}
                onChange={handleChange}
                disabled={!form.customerId || !!editingItem} // Disable if editing
                className={styles.select}
              >
                <option value="">Select Invoice</option>
                {filteredInvoices.map((invoice) => (
                  <option key={invoice.id} value={invoice.id}>
                    {invoice.invoice_number} - {formatCurrency(invoice.total_amount)} ({(formatDate(invoice.created_at))})
                  </option>
                ))}
              </select>
            </div>
          )} */}

          {/* Amount */}
          <div className={styles.formGroup}>
            <label htmlFor="amount" className={styles.label}>
              Amount Received <span className={styles.required}>*</span>
            </label>
           
              <input
                id="amount"
                name="amount"
                type="number"
                step="0.01"
                min="0"
                value={form.amount}
                onChange={handleInputChange}
                required
                placeholder="0.00"
                className={styles.input}
              />
            
          </div>

          {/* Payment Date */}
          {/* <div className={styles.formGroup}>
            <label htmlFor="paymentDate" className={styles.label}>
              Payment Date <span className={styles.required}>*</span>
            </label>
            <input
              id="paymentDate"
              name="paymentDate"
              type="date"
              value={form.paymentDate}
              onChange={handleInputChange}
              required
              className={styles.input}
            />
          </div> */}

          {/* Payment Method */}
          <div className={styles.formGroup}>
            <label htmlFor="paymentMethod" className={styles.label}>
              Payment Method <span className={styles.required}>*</span>
            </label>
            <select
              id="paymentMethod"
              name="paymentMethod"
              value={form.paymentMethod}
              onChange={handleChange}
              required
              className={styles.select}
            >
              {paymentMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label}
                </option>
              ))}
            </select>
          </div>

          {/* Account Head */}
          {/* <div className={styles.formGroup}>
            <label htmlFor="accountHead" className={styles.label}>
              Account Head <span className={styles.required}>*</span>
            </label>
            <select
              id="accountHead"
              name="accountHead"
              value={selectedAccountHead}
              onChange={e => setSelectedAccountHead(e.target.value)}
              required
              className={styles.select}
            >
              <option value="">Select Account Head</option>
              {accountHeads.map((head) => (
                <option key={head.id} value={head.id}>
                  {head.name}
                </option>
              ))}
            </select>
          </div> */}

          {/* Notes */}
          <div className={styles.formGroup}>
            <label htmlFor="notes" className={styles.label}>
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={form.notes}
              onChange={handleInputChange}
              placeholder="Add any additional notes (optional)"
              className={styles.textarea}
              rows={3}
            />
          </div>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <Button 
            type="submit" 
            variant="primary" 
            disabled={isSubmitting}
            className={styles.submitButton}
          >
            {isSubmitting 
              ? (editingItem ? 'Updating...' : 'Recording Payment...') 
              : (editingItem ? 'Update Payment' : 'Record Payment')
            }
          </Button>
        </div>

        {/* Status Messages */}
        {success && (
          <div className={styles.successMessage}>
            ✓ Payment recorded successfully!
          </div>
        )}
        
        {error && (
          <div className={styles.errorMessage}>
            ✗ {error}
          </div>
        )}
      </form>

      {/* Payment Summary */}
      {success && (
        <div className={styles.summary}>
          <h3>Payment Summary</h3>
          <div className={styles.summaryGrid}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Customer:</span>
              <span className={styles.summaryValue}>
                {customers.find(c => c.id === form.customerId)?.name || '-'}
              </span>
            </div>
            {/* <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Invoice:</span>
              <span className={styles.summaryValue}>
                {invoices.find(i => i.id === form.invoiceId)?.invoice_number || 'General Payment'}
              </span>
            </div> */}
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Amount:</span>
              <span className={styles.summaryValue}>
                ${parseFloat(form.amount || '0').toFixed(2)}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Date:</span>
              <span className={styles.summaryValue}>
                {new Date(form.paymentDate).toLocaleDateString()}
              </span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Method:</span>
              <span className={styles.summaryValue}>
                {paymentMethods.find(pm => pm.value === form.paymentMethod)?.label || '-'}
              </span>
            </div>
            {/* <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Account Head:</span>
              <span className={styles.summaryValue}>
                {accountHeads.find(head => head.id === selectedAccountHead)?.name || '-'}
              </span>
            </div> */}
            {form.notes && (
              <div className={styles.summaryItem}>
                <span className={styles.summaryLabel}>Notes:</span>
                <span className={styles.summaryValue}>{form.notes}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceivedAmountForm;
